import { useState, useEffect } from 'react';

interface ContactInfo {
  phones: string[];
  emails: string[];
  address: string;
  schedule: {
    weekdays: string;
    saturday: string;
    sunday: string;
  };
  map_url: string;
}

interface AboutUs {
  title: string;
  description: string;
  mission: string;
  vision: string;
  values: string[];
  stats: {
    years_experience: string;
    properties_sold: string;
    happy_clients: string;
    team_members: string;
  };
}

export default function SiteSettings() {
  const [activeTab, setActiveTab] = useState<'contact' | 'about'>('contact');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Contact Info State
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phones: ['', ''],
    emails: ['', ''],
    address: '',
    schedule: {
      weekdays: '',
      saturday: '',
      sunday: ''
    },
    map_url: ''
  });

  // About Us State
  const [aboutUs, setAboutUs] = useState<AboutUs>({
    title: '',
    description: '',
    mission: '',
    vision: '',
    values: ['', '', '', '', ''],
    stats: {
      years_experience: '',
      properties_sold: '',
      happy_clients: '',
      team_members: ''
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/site-config-api`);
      const result = await response.json();

      if (result.success) {
        if (result.data.contact_info) {
          setContactInfo(result.data.contact_info);
        }
        if (result.data.about_us) {
          setAboutUs(result.data.about_us);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      alert('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const saveContactInfo = async () => {
    setSaving(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/site-config-api`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: 'contact_info',
          value: contactInfo,
          description: 'Información de contacto del sitio'
        })
      });

      const result = await response.json();
      if (result.success) {
        alert('Información de contacto guardada exitosamente');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error saving contact info:', error);
      alert('Error al guardar la información de contacto');
    } finally {
      setSaving(false);
    }
  };

  const saveAboutUs = async () => {
    setSaving(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/site-config-api`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: 'about_us',
          value: aboutUs,
          description: 'Información de acerca de nosotros'
        })
      });

      const result = await response.json();
      if (result.success) {
        alert('Información de "Acerca de Nosotros" guardada exitosamente');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error saving about us:', error);
      alert('Error al guardar la información de "Acerca de Nosotros"');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Configuración del Sitio</h2>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('contact')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'contact'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="ri-phone-line mr-2"></i>
            Información de Contacto
          </button>
          <button
            onClick={() => setActiveTab('about')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'about'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="ri-information-line mr-2"></i>
            Acerca de Nosotros
          </button>
        </nav>
      </div>

      {/* Contact Info Tab */}
      {activeTab === 'contact' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Teléfonos</h3>
            <div className="space-y-3">
              {contactInfo.phones.map((phone, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono {index + 1}
                  </label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => {
                      const newPhones = [...contactInfo.phones];
                      newPhones[index] = e.target.value;
                      setContactInfo({ ...contactInfo, phones: newPhones });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+34 91 234 56 78"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Correos Electrónicos</h3>
            <div className="space-y-3">
              {contactInfo.emails.map((email, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email {index + 1}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const newEmails = [...contactInfo.emails];
                      newEmails[index] = e.target.value;
                      setContactInfo({ ...contactInfo, emails: newEmails });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="info@grupofr.com"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dirección
            </label>
            <input
              type="text"
              value={contactInfo.address}
              onChange={(e) => setContactInfo({ ...contactInfo, address: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Calle Gran Vía, 123, 28013 Madrid, España"
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Horarios de Atención</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lunes - Viernes
                </label>
                <input
                  type="text"
                  value={contactInfo.schedule.weekdays}
                  onChange={(e) => setContactInfo({
                    ...contactInfo,
                    schedule: { ...contactInfo.schedule, weekdays: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="9:00 - 19:00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sábados
                </label>
                <input
                  type="text"
                  value={contactInfo.schedule.saturday}
                  onChange={(e) => setContactInfo({
                    ...contactInfo,
                    schedule: { ...contactInfo.schedule, saturday: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="10:00 - 14:00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domingos
                </label>
                <input
                  type="text"
                  value={contactInfo.schedule.sunday}
                  onChange={(e) => setContactInfo({
                    ...contactInfo,
                    schedule: { ...contactInfo.schedule, sunday: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Cerrado"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL del Mapa de Google (iframe embed)
            </label>
            <textarea
              value={contactInfo.map_url}
              onChange={(e) => setContactInfo({ ...contactInfo, map_url: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://www.google.com/maps/embed?pb=..."
            />
            <p className="mt-1 text-sm text-gray-500">
              Obtén la URL desde Google Maps &gt; Compartir &gt; Insertar un mapa
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveContactInfo}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* About Us Tab */}
      {activeTab === 'about' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título
            </label>
            <input
              type="text"
              value={aboutUs.title}
              onChange={(e) => setAboutUs({ ...aboutUs, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Acerca de GRUPO FR"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción Principal
            </label>
            <textarea
              value={aboutUs.description}
              onChange={(e) => setAboutUs({ ...aboutUs, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Descripción breve de la empresa..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Misión
            </label>
            <textarea
              value={aboutUs.mission}
              onChange={(e) => setAboutUs({ ...aboutUs, mission: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nuestra misión es..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Visión
            </label>
            <textarea
              value={aboutUs.vision}
              onChange={(e) => setAboutUs({ ...aboutUs, vision: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nuestra visión es..."
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Valores</h3>
            <div className="space-y-3">
              {aboutUs.values.map((value, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor {index + 1}
                  </label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => {
                      const newValues = [...aboutUs.values];
                      newValues[index] = e.target.value;
                      setAboutUs({ ...aboutUs, values: newValues });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ingresa un valor de la empresa"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Estadísticas</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Años de Experiencia
                </label>
                <input
                  type="text"
                  value={aboutUs.stats.years_experience}
                  onChange={(e) => setAboutUs({
                    ...aboutUs,
                    stats: { ...aboutUs.stats, years_experience: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="20+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Propiedades Vendidas
                </label>
                <input
                  type="text"
                  value={aboutUs.stats.properties_sold}
                  onChange={(e) => setAboutUs({
                    ...aboutUs,
                    stats: { ...aboutUs.stats, properties_sold: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="5000+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clientes Satisfechos
                </label>
                <input
                  type="text"
                  value={aboutUs.stats.happy_clients}
                  onChange={(e) => setAboutUs({
                    ...aboutUs,
                    stats: { ...aboutUs.stats, happy_clients: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="3500+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Miembros del Equipo
                </label>
                <input
                  type="text"
                  value={aboutUs.stats.team_members}
                  onChange={(e) => setAboutUs({
                    ...aboutUs,
                    stats: { ...aboutUs.stats, team_members: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="50+"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveAboutUs}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}